import { useState, useEffect } from 'react';
import { useAuth } from '../../AuthProvider';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { TeamPermissions } from './TeamPermissions';
import { InfoTooltip } from './InfoTooltip';
import { PermissionsInfoGroup } from './PermissionsInfoGroup';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Users, 
  UserPlus, 
  Mail, 
  Crown, 
  Shield, 
  User, 
  Trash2, 
  Book, 
  Eye, 
  Edit, 
  Settings,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface TeamMember {
  id: string;
  email: string;
  full_name: string | null;
  role_type: 'owner' | 'admin' | 'member';
  invited_by: string | null;
  invited_at: string | null;
  accepted_at: string | null;
  created_at: string;
}

interface TeamInvitation {
  id: string;
  email: string;
  role_type: 'admin' | 'member';
  expires_at: string;
  created_at: string;
  invited_by: {
    full_name: string | null;
    email: string;
  };
}

interface BookPermission {
  id: string;
  permission_type: 'read' | 'write' | 'admin';
  created_at: string;
  user: {
    id: string;
    email: string;
    full_name: string | null;
  };
  granted_by: {
    full_name: string | null;
    email: string;
  };
}

export function TeamManagement() {
  const { session, user } = useAuth();
  const { subscription, planLimits } = usePlanLimits();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'members' | 'invitations' | 'permissions'>('members');
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviting, setInviting] = useState(false);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';

  useEffect(() => {
    loadTeamData();
  }, [session]);

  useEffect(() => {
    // Reset to members tab if current tab is not accessible
    if (currentUserRole && !['owner', 'admin'].includes(currentUserRole)) {
      if (activeTab === 'invitations' || activeTab === 'permissions') {
        setActiveTab('members');
      }
    }
  }, [currentUserRole, activeTab]);

  // Handle query parameters
  useEffect(() => {
    const inviteParam = searchParams.get('invite');
    const focusParam = searchParams.get('focus');

    if (inviteParam === 'true') {
      console.log('📝 Opening invite modal from query params');
      setShowInviteModal(true);
      
      // Clear the query params after opening modal
      navigate(location.pathname, { replace: true });
      
      // Focus on first input after modal opens
      if (focusParam === 'true') {
        setTimeout(() => {
          const emailInput = document.querySelector('#invite-email-input') as HTMLInputElement;
          if (emailInput) {
            emailInput.focus();
          }
        }, 100);
      }
    }
  }, [searchParams, navigate, location.pathname]);

  const loadTeamData = async () => {
    if (!session?.access_token) return;

    try {
      setLoading(true);
      setError(null);

      // Load team members first
      const membersResponse = await fetch(`${API_BASE}/api/team/members`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (membersResponse.ok) {
        const membersData = await membersResponse.json();
        setMembers(membersData);
        
        // Find current user's role
        const currentUser = membersData.find((m: TeamMember) => m.id === user?.id);
        let userRole: string | null = null;
        
        if (currentUser) {
          userRole = currentUser.role_type;
          setCurrentUserRole(currentUser.role_type);
      }

        // Load invitations - only if user is owner or admin
        if (userRole && ['owner', 'admin'].includes(userRole)) {
          console.log('🔍 [TEAM-FRONTEND] User is admin/owner, loading invitations...')
          console.log('🔍 [TEAM-FRONTEND] Current user role:', userRole)
          
      const invitationsResponse = await fetch(`${API_BASE}/api/team/invitations`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

          console.log('🔍 [TEAM-FRONTEND] Invitations response status:', invitationsResponse.status)

      if (invitationsResponse.ok) {
        const invitationsData = await invitationsResponse.json();
        setInvitations(invitationsData);
            console.log('✅ [TEAM-FRONTEND] Loaded invitations:', invitationsData.length)
          } else {
            console.error('❌ [TEAM-FRONTEND] Error loading invitations:', invitationsResponse.status)
            setInvitations([]);
          }
        } else {
          console.log('⚠️ [TEAM-FRONTEND] User is not admin/owner (role:', userRole, '), skipping invitations')
          setInvitations([]);
        }
      } else {
        console.error('❌ [TEAM-FRONTEND] Error loading team members:', membersResponse.status)
      }
    } catch (error) {
      console.error('Error loading team data:', error);
      setError('Fehler beim Laden der Team-Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!session?.access_token || !inviteEmail) return;

    try {
      setInviting(true);
      setError(null);

      console.log('🔍 [FRONTEND] Sending invitation request:', {
        email: inviteEmail,
        role_type: inviteRole
      });

      const response = await fetch(`${API_BASE}/api/team/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: inviteEmail,
          role_type: inviteRole
        })
      });

      console.log('🔍 [FRONTEND] Response status:', response.status);
      
      const responseData = await response.json();
      console.log('🔍 [FRONTEND] Response data:', responseData);

      if (response.ok) {
        setSuccess('Einladung erfolgreich versendet!');
        toast.success('Einladung gesendet');
        setShowInviteModal(false);
        setInviteEmail('');
        setInviteRole('member');
        await loadTeamData();
      } else {
        // Show the specific error message from the backend
        const msg = responseData.error || responseData.message || 'Fehler beim Versenden der Einladung';
        setError(msg);
        toast.error(msg);
      }
    } catch (error) {
      console.error('Error inviting user:', error);
      setError('Netzwerkfehler beim Versenden der Einladung');
      toast.error('Netzwerkfehler beim Versenden der Einladung');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!session?.access_token) return;
    if (!confirm('Möchten Sie dieses Teammitglied wirklich entfernen?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/team/members/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        setSuccess('Teammitglied erfolgreich entfernt');
        await loadTeamData();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Fehler beim Entfernen des Teammitglieds');
      }
    } catch (error) {
      console.error('Error removing member:', error);
      setError('Fehler beim Entfernen des Teammitglieds');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!session?.access_token) return;
    if (!confirm('Möchten Sie diese Einladung wirklich zurückziehen?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/team/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        setSuccess('Einladung erfolgreich zurückgezogen');
        await loadTeamData();
      } else if (response.status === 404) {
        setError('Einladung nicht gefunden - möglicherweise bereits gelöscht');
        await loadTeamData(); // Refresh to update the UI
      } else {
        // Try to parse JSON, but handle HTML responses
        let errorMessage = 'Fehler beim Zurückziehen der Einladung';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          // Response is not JSON (probably HTML error page)
          errorMessage = `Server-Fehler (${response.status}): Endpoint möglicherweise nicht verfügbar`;
        }
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Error canceling invitation:', error);
      setError('Netzwerkfehler beim Zurückziehen der Einladung');
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'admin': return <Shield className="w-4 h-4 text-blue-500" />;
      case 'member': return <User className="w-4 h-4 text-gray-500" />;
      default: return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleName = (role: string) => {
    switch (role) {
      case 'owner': return 'Inhaber';
      case 'admin': return 'Administrator';
      case 'member': return 'Mitglied';
      default: return role;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              Team Management
              <PermissionsInfoGroup />
            </h1>
          </div>
          {currentUserRole && ['owner', 'admin'].includes(currentUserRole) && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Benutzer einladen
          </button>
          )}
        </div>
      </div>

      {/* Seat Limit Info */}
      {planLimits && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                Team-Mitglieder: {members.length} / {planLimits.seats_limit}
              </span>
            </div>
            {members.length >= planLimits.seats_limit && (
              <div className="flex items-center space-x-2">
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="text-sm text-red-600">Limit erreicht</span>
                <a
                  href="/qr/pricing"
                  className="ml-2 text-sm text-blue-600 hover:text-blue-700 underline"
                >
                  Plan upgraden
                </a>
              </div>
            )}
          </div>
          <div className="mt-2">
            <div className="w-full bg-blue-100 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  members.length >= planLimits.seats_limit ? 'bg-red-500' : 'bg-blue-600'
                }`}
                style={{ width: `${Math.min((members.length / planLimits.seats_limit) * 100, 100)}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {[
              { id: 'members', label: 'Teammitglieder', icon: Users, show: true },
              { id: 'invitations', label: 'Einladungen', icon: Mail, show: currentUserRole && ['owner', 'admin'].includes(currentUserRole) },
              { id: 'permissions', label: 'Berechtigungen', icon: Settings, show: currentUserRole && ['owner', 'admin'].includes(currentUserRole) }
            ].filter(tab => tab.show).map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`
                    flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors
                    ${isActive 
                      ? 'border-blue-500 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.label}
                  {tab.id === 'invitations' && invitations.length > 0 && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {invitations.length}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'members' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                Teammitglieder ({members.length})
              </h3>
              
              {members.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Noch keine Teammitglieder vorhanden
                </div>
              ) : (
                <div className="space-y-3">
                  {members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        {getRoleIcon(member.role_type)}
                        <div>
                          <p className="font-medium text-gray-900">
                            {member.full_name || member.email}
                          </p>
                          <p className="text-sm text-gray-500">{member.email}</p>
                          <p className="text-xs text-gray-400">
                            {getRoleName(member.role_type)} • 
                            Beigetreten: {new Date(member.created_at).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                      </div>
                      
                      {member.role_type !== 'owner' && currentUserRole && ['owner', 'admin'].includes(currentUserRole) && (
                        <button
                          onClick={() => handleRemoveMember(member.id)}
                          className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                          title="Teammitglied entfernen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'invitations' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Ausstehende Einladungen ({invitations.length})</h3>
              
              {invitations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Keine ausstehenden Einladungen
                </div>
              ) : (
                <div className="space-y-3">
                  {invitations.map((invitation) => (
                    <div key={invitation.id} className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <div className="flex items-center space-x-3">
                        <Clock className="w-5 h-5 text-yellow-600" />
                        <div>
                          <p className="font-medium text-gray-900">{invitation.email}</p>
                          <p className="text-sm text-gray-500">
                            Rolle: {getRoleName(invitation.role_type)} • 
                            Eingeladen von: {invitation.invited_by.full_name || invitation.invited_by.email}
                          </p>
                          <p className="text-xs text-gray-400">
                            Läuft ab: {new Date(invitation.expires_at).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleCancelInvitation(invitation.id)}
                        className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                        title="Einladung zurückziehen"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'permissions' && (
            <TeamPermissions />
          )}
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Benutzer einladen
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-Mail-Adresse
                </label>
                <input
                  id="invite-email-input"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="benutzer@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rolle
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="member">Mitglied</option>
                  <option value="admin">Administrator</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Administratoren können andere Benutzer einladen und Berechtigungen verwalten
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                  setInviteRole('member');
                }}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Abbrechen
              </button>
              <button
                onClick={handleInviteUser}
                disabled={inviting || !inviteEmail}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {inviting ? 'Wird eingeladen...' : 'Einladen'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
} 
